import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';

/**
 * The hero backdrop: accounts as nodes, money moving between them.
 *
 * WHY THIS IS DRAWN RATHER THAN PHOTOGRAPHED. A stock "network mesh" image
 * shows a generic abstraction of connectivity; this shows the actual product
 * -- chequing, brokerages, property and loans, with value flowing between
 * them into one number. It also stays sharp on any display, weighs a few KB
 * against a few hundred, and carries no licence.
 *
 * IT MUST NOT COMPETE WITH THE HEADLINE. Everything here sits at low opacity
 * behind text that has to clear 4.5:1, and the mesh fades out toward the left
 * where the copy sits. A backdrop that wins the eye has failed.
 *
 * Motion is slow and non-looping-to-the-eye: value drifts along an edge over
 * several seconds rather than pulsing. Anything faster reads as a loading
 * state. Fully disabled under prefers-reduced-motion, where the mesh renders
 * as a still diagram and loses nothing.
 */

interface Node {
  id: string;
  x: number;
  y: number;
  r: number;
  /** Larger nodes read as the accounts holding more. */
  weight: number;
}

// Positions are hand-placed, not random: a real portfolio is lopsided, and an
// evenly scattered graph looks like a screensaver.
const NODES: Node[] = [
  { id: 'rrsp', x: 620, y: 150, r: 26, weight: 1 },
  { id: 'tfsa', x: 470, y: 250, r: 19, weight: 0.8 },
  { id: 'margin', x: 745, y: 268, r: 22, weight: 0.9 },
  { id: 'chequing', x: 545, y: 385, r: 14, weight: 0.6 },
  { id: 'property', x: 830, y: 148, r: 17, weight: 0.75 },
  { id: 'crypto', x: 682, y: 432, r: 11, weight: 0.5 },
  { id: 'resp', x: 392, y: 152, r: 12, weight: 0.55 },
  { id: 'loan', x: 866, y: 378, r: 13, weight: 0.6 },
];

const EDGES: Array<[string, string]> = [
  ['rrsp', 'tfsa'], ['rrsp', 'margin'], ['rrsp', 'property'],
  ['tfsa', 'chequing'], ['tfsa', 'resp'], ['margin', 'chequing'],
  ['margin', 'loan'], ['chequing', 'crypto'], ['margin', 'property'],
  ['chequing', 'loan'], ['rrsp', 'resp'],
];

const byId = (id: string) => NODES.find((n) => n.id === id)!;

export const AccountMesh: React.FC = () => {
  const wrapRef = useRef<HTMLDivElement | null>(null);

  /**
   * Scroll-driven depth.
   *
   * Rotates the mesh a few degrees and slides each depth layer at its own
   * rate, so the nodes separate as the page moves. Kept SMALL on purpose --
   * past roughly 8 degrees the nodes stop reading as a diagram and start
   * reading as a tilted photograph of one.
   *
   * Written to CSS custom properties from a rAF, never to React state: this
   * runs on every scroll frame, and a re-render per frame is how a hero
   * becomes the reason a page feels slow. transform only -- no width, height
   * or top, so nothing lays out.
   */
  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    let frame = 0;
    const apply = () => {
      frame = 0;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight || 1;
      // -1 above the fold, 0 centred, 1 below. Clamped so the mesh settles
      // rather than continuing to rotate off-screen.
      const p = Math.max(-1, Math.min(1, (rect.top + rect.height / 2 - vh / 2) / vh));
      el.style.setProperty('--mesh-rx', `${(-p * 7).toFixed(2)}deg`);
      el.style.setProperty('--mesh-ry', `${(p * 5).toFixed(2)}deg`);
      el.style.setProperty('--mesh-shift', `${(p * 26).toFixed(1)}px`);
    };
    const onScroll = () => {
      if (frame) return;
      frame = window.requestAnimationFrame(apply);
    };

    apply();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    return () => {
      if (frame) window.cancelAnimationFrame(frame);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
    };
  }, []);

  // Stable per-edge timing so the flows do not pulse in unison, which reads
  // as a progress indicator rather than movement.
  const edges = useMemo(
    () => EDGES.map(([a, b], i) => {
      const from = byId(a);
      const to = byId(b);
      return {
        key: `${a}-${b}`,
        d: `M ${from.x} ${from.y} L ${to.x} ${to.y}`,
        dur: 6 + ((i * 1.7) % 5),
        delay: (i * 0.9) % 6,
      };
    }),
    [],
  );

  return (
    <Box
      ref={wrapRef}
      aria-hidden="true"
      sx={{
        // Perspective on the wrapper, transform on the SVG: rotating a child
        // inside a perspective parent is what gives real depth rather than a
        // flat skew.
        perspective: '1100px',
        '& .mesh-stage': {
          transformStyle: 'preserve-3d',
          transform:
            'rotateX(var(--mesh-rx, 0deg)) rotateY(var(--mesh-ry, 0deg))',
          transition: 'transform 120ms linear',
          willChange: 'transform',
        },
        // Layers move at different rates, which is the parallax. Nearer
        // (larger) nodes travel furthest, and the edges drift the other way so
        // the mesh opens up rather than sliding as one picture.
        //
        // 2D translate ONLY on these. They are SVG <g> elements, and 3D
        // transforms inside an SVG are flattened by Safari and Firefox -- the
        // depth would silently do nothing in two of three browsers. The
        // rotation above is on the <svg> element itself, which is real HTML
        // and honours perspective everywhere.
        '& .depth-near': {
          transform: 'translateY(calc(var(--mesh-shift, 0px) * -1))',
          transition: 'transform 120ms linear',
        },
        '& .depth-mid': {
          transform: 'translateY(calc(var(--mesh-shift, 0px) * -0.45))',
          transition: 'transform 120ms linear',
        },
        '& .depth-far': {
          transform: 'translateY(calc(var(--mesh-shift, 0px) * 0.4))',
          transition: 'transform 120ms linear',
        },
        position: 'absolute',
        inset: 0,
        overflow: 'hidden',
        pointerEvents: 'none',
        // Decorative only, and never at the cost of the headline's contrast.
        opacity: { xs: 0.35, md: 0.55 },
        // Hidden on the narrowest screens: at phone width the copy fills the
        // viewport and the mesh would sit directly under the text.
        display: { xs: 'none', sm: 'block' },
        maskImage:
          'linear-gradient(to right, transparent 0%, transparent 22%, black 60%, black 100%)',
        WebkitMaskImage:
          'linear-gradient(to right, transparent 0%, transparent 22%, black 60%, black 100%)',
        '@media (prefers-reduced-motion: reduce)': {
          '& .flow': { display: 'none' },
          // No scroll listener runs, but the transforms must not linger
          // either -- a mesh frozen mid-tilt looks broken rather than still.
          '& .mesh-stage, & .depth-near, & .depth-mid, & .depth-far': {
            transform: 'none',
          },
        },
      }}
    >
      <svg
        viewBox="0 0 960 520"
        preserveAspectRatio="xMidYMid slice"
        className="mesh-stage"
        style={{ width: '100%', height: '100%', display: 'block' }}
        focusable="false"
      >
        <defs>
          <radialGradient id="mesh-node" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="var(--inflow, #2E9E6B)" stopOpacity="0.9" />
            <stop offset="100%" stopColor="var(--inflow, #2E9E6B)" stopOpacity="0.15" />
          </radialGradient>
          <filter id="mesh-glow" x="-60%" y="-60%" width="220%" height="220%">
            <feGaussianBlur stdDeviation="7" result="b" />
            <feMerge>
              <feMergeNode in="b" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Edges first, so nodes sit on top of the lines that join them. */}
        <g className="depth-far" stroke="var(--inflow, #2E9E6B)" strokeOpacity="0.22" strokeWidth="1">
          {edges.map((e) => <path key={e.key} d={e.d} fill="none" />)}
        </g>

        {/* Value moving between accounts. One dot per edge -- a stream of them
            reads as network traffic, which is not what this is about. */}
        <g className="flow depth-mid">
          {edges.map((e) => (
            <circle key={e.key} r="2.6" fill="var(--inflow, #2E9E6B)" opacity="0.85">
              <animateMotion
                dur={`${e.dur}s`}
                begin={`${e.delay}s`}
                repeatCount="indefinite"
                path={e.d}
                rotate="auto"
              />
            </circle>
          ))}
        </g>

        <g filter="url(#mesh-glow)">
          {NODES.map((n) => (
            <g key={n.id} className={n.r >= 19 ? 'depth-near' : 'depth-mid'}>
              <circle cx={n.x} cy={n.y} r={n.r} fill="url(#mesh-node)" />
              <circle
                cx={n.x}
                cy={n.y}
                r={n.r}
                fill="none"
                stroke="var(--inflow, #2E9E6B)"
                strokeOpacity={0.35 + n.weight * 0.3}
                strokeWidth="1"
              />
              <circle cx={n.x} cy={n.y} r={2.2} fill="var(--inflow, #2E9E6B)" opacity="0.9" />
            </g>
          ))}
        </g>
      </svg>
    </Box>
  );
};
